import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import ReactGA4 from 'react-ga4';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  Outlet,
  redirect
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Typography } from '@mui/material';
import { EstudiApiQueryClient } from './services/adapters/EstudiApiQueryClient';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { EstudiAuthApiClient } from './services/adapters/EstudiAuthApiClient';
import TeacherSearchProvider from './providers/TeacherSearchProvider';
import ReservationProvider from './providers/ReservationProvider';
import StudentUserProvider from './providers/StudentUserProvider';
import AuthenticatedRoute from './components/Auth/AuthenticatedRoute/AuthenticatedRoute';
import AuthUserProvider from './providers/AuthUserProvider';
import Outline from './components/Outline';
import ErrorPage from './components/Errors';
import Home from './components/Home';
import { loader as resultsLoader } from './components/Results/Results.loader';
import Results from './components/Results';
import { loader as teacherProfileLoader } from './components/TeacherProfile/TeacherProfile.loader';
import TeacherProfile from './components/TeacherProfile';
import { loader as editTeacherProfileLoader } from './components/TeacherProfile/EditTeacherProfile/EditTeacherProfile.loader';
import EditTeacherProfile from './components/TeacherProfile/EditTeacherProfile/EditTeacherProfile';
import Help from './components/Help';
import HelpPage from './components/Help/HelpPage';
import TeacherRequest from './components/Help/TeacherRequest';
import FAQ from './components/Help/FAQ/FAQ';
import TelegramGroupsHelp from './components/Help/TelegramGroupsHelp';
import ComplaintForm from './components/Help/ComplaintForm';
import Landing from './components/Landing';
import Login from './components/Auth/Login';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Search from './components/Search';
import TeachersLanding from './components/TeachersLanding';
import UserInteractionHistoryProvider from './providers/UserInteractionHistoryProvider';
import { loader as reservationDetailLoader } from './components/ReservationDetail/ReservationDetail.loader';
import ReservationDetail from './components/ReservationDetail';
import './Variables.css';
import './index.css';

//FixMe: Privacy and GDPR Compliance:
// Ensure that you follow privacy regulations like GDPR when collecting user data.
// Provide users with the option to opt out of tracking if required.
ReactGA4.initialize(process.env.REACT_APP_GA_TRACKING_ID!);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const queryClient = new QueryClient();
const estudiClient = new EstudiApiQueryClient(queryClient, new EstudiAuthApiClient());

const PageViewTracker = ({
  outline,
  customComponent
}: {
  outline: boolean;
  customComponent?: React.ReactNode;
}) => {
  const location = useLocation();

  useEffect(() => {
    ReactGA4.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);

  return outline ? <Outline customComponent={customComponent} /> : <Outlet />;
};

const HelpPages = {
  claims: {
    content: (
      <div>
        <Typography
          variant="h6"
          component="div"
          align="left"
          sx={{
            marginLeft: '3%',
            fontSize: {
              sm: '1rem',
              xs: '0.75rem'
            }
          }}>
          ¿Tuviste un problema? Dejanos tu reclamo y te contactamos.
        </Typography>
        <ComplaintForm />
      </div>
    )
  },
  request: {
    content: <TeacherRequest />
  },
  faq: {
    content: <FAQ />
  },
  telegramGroups: {
    content: <TelegramGroupsHelp />
  }
};
const router = createBrowserRouter([
  {
    path: '/',
    element: <PageViewTracker outline={true} />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <Home />
      }
    ]
  },
  {
    path: '/teachers',
    element: <PageViewTracker outline={true} customComponent={<Search />} />,
    children: [
      {
        path: '/teachers',
        loader: resultsLoader(estudiClient),
        element: <Results />
      }
    ]
  },
  {
    path: '/teachers/about',
    element: <PageViewTracker outline={true} />,
    children: [
      {
        path: '/teachers/about',
        element: <TeachersLanding />
      }
    ]
  },
  {
    path: '/teachers/:teacherId',
    element: <PageViewTracker outline={true} />,
    children: [
      {
        path: '/teachers/:teacherId/profile',
        loader: teacherProfileLoader(estudiClient),
        element: <TeacherProfile />
      },
      {
        path: '/teachers/:teacherId/profile/edit',
        loader: editTeacherProfileLoader(estudiClient),
        element: (
          <AuthenticatedRoute>
            <EditTeacherProfile estudiClient={estudiClient} />
          </AuthenticatedRoute>
        )
      }
    ]
  },
  {
    path: '/help',
    element: <PageViewTracker outline={true} />,
    children: [
      {
        path: '/help',
        element: <Help />
      },
      {
        path: '/help/claims',
        element: <HelpPage page={{ title: 'Reclamos', content: HelpPages.claims.content }} />
      },
      {
        path: '/help/request',
        element: <PageViewTracker outline={false} />,
        children: [
          {
            path: '/help/request',
            element: (
              <HelpPage
                page={{ title: 'Encontremos a tu profe ideal', content: HelpPages.request.content }}
              />
            )
          }
        ]
      },
      {
        path: '/help/faq',
        element: (
          <HelpPage page={{ title: 'Preguntas frecuentes', content: HelpPages.faq.content }} />
        )
      },
      {
        path: '/help/telegram',
        element: (
          <HelpPage
            page={{
              title: 'Ayuda con Grupos de Telegram',
              content: HelpPages.telegramGroups.content
            }}
          />
        )
      }
    ]
  },
  {
    path: '/about',
    element: <PageViewTracker outline={false} />,
    children: [
      {
        path: '/about',
        element: <Landing />
      }
    ]
  },
  {
    path: '/terms',
    element: <PageViewTracker outline={true} />,
    children: [
      {
        path: '/terms',
        element: <Terms />
      }
    ]
  },
  {
    path: '/privacy',
    element: <PageViewTracker outline={true} />,
    children: [
      {
        path: '/privacy',
        element: <Privacy />
      }
    ]
  },
  {
    path: '/schools',
    loader: () => redirect('/?type=schools')
  },
  {
    path: '/auth',
    element: <PageViewTracker outline={true} />,
    children: [
      {
        path: '/auth/login',
        element: <Login />
      }
    ]
  },
  {
    path: '/reservation/:reservationId',
    loader: ({ params }) => redirect(`/reservations/${params.reservationId}`)
  },
  {
    path: '/reservations/:reservationId',
    element: <PageViewTracker outline={true} />,
    children: [
      {
        path: '/reservations/:reservationId',
        loader: reservationDetailLoader(estudiClient),
        element: <ReservationDetail estudiClient={estudiClient} />
      }
    ]
  }
]);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <UserInteractionHistoryProvider>
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID!}>
          <AuthUserProvider estudiClient={estudiClient}>
            <TeacherSearchProvider>
              <ReservationProvider>
                <StudentUserProvider>
                  <RouterProvider router={router} />
                </StudentUserProvider>
              </ReservationProvider>
            </TeacherSearchProvider>
          </AuthUserProvider>
        </GoogleOAuthProvider>
      </UserInteractionHistoryProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
